import React, { useState } from 'react';
import { QuestionDefinition, DosingScheduleValue, MeasurementValue } from '../../../types/survey.types';
import {
  TextInput,
  TextArea,
  Dropdown,
  RadioGroup,
  MultiSelect,
  DropdownOption,
  RadioOption,
  MultiSelectOption,
} from '../../common/FormElements';

interface QuestionPreviewProps {
  question: QuestionDefinition;
}

const QuestionPreview: React.FC<QuestionPreviewProps> = ({ question }) => {
  // State for preview interactions (not saved, just for demo)
  const [previewValue, setPreviewValue] = useState<any>('');
  const [previewMultiValue, setPreviewMultiValue] = useState<string[]>([]);

  const renderPreviewInput = () => {
    switch (question.type) {
      case 'text':
      case 'email':
        return (
          <TextInput
            id={`preview-${question.id}`}
            label={question.title}
            value={previewValue}
            onChange={setPreviewValue}
            placeholder={question.placeholder || ''}
            required={question.required}
            type={question.type === 'email' ? 'email' : 'text'}
            helpText={question.settings?.helpText}
            maxLength={question.settings?.maxLength}
          />
        );

      case 'textarea':
        return (
          <TextArea
            id={`preview-${question.id}`}
            label={question.title}
            value={previewValue}
            onChange={setPreviewValue}
            placeholder={question.placeholder || ''}
            required={question.required}
            helpText={question.settings?.helpText}
            rows={question.settings?.rows || 4}
            maxLength={question.settings?.maxLength}
            showCharCount={!!question.settings?.maxLength}
          />
        );

      case 'number':
        const numberHelpText = question.settings?.helpText;
        return (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {question.title}
              {question.required && <span className="text-red-500 ml-1">*</span>}
            </label>
            {numberHelpText && (
              <p className="text-sm text-gray-500 mb-2">{numberHelpText}</p>
            )}
            <input
              type="number"
              value={previewValue}
              onChange={(e) => setPreviewValue(e.target.value)}
              placeholder={question.placeholder || ''}
              min={question.settings?.minValue}
              max={question.settings?.maxValue}
              step={question.settings?.step || 1}
              required={question.required}
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary"
            />
          </div>
        );

      case 'radio':
        const radioOptions: RadioOption[] = question.options?.map(opt => ({
          value: opt.value,
          label: opt.label,
        })) || [];

        return (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {question.title}
              {question.required && <span className="text-red-500 ml-1">*</span>}
            </label>
            <p className="text-sm text-gray-600 mb-3">Select one option</p>
            <RadioGroup
              id={`preview-${question.id}`}
              label=""
              value={previewValue}
              onChange={setPreviewValue}
              options={radioOptions}
              required={question.required}
              helpText={question.settings?.helpText}
              layout={question.settings?.layout as 'vertical' | 'horizontal' || 'vertical'}
            />
          </div>
        );

      case 'checkbox':
        const checkboxOptions: MultiSelectOption[] = question.options?.map(opt => ({
          value: opt.value,
          label: opt.label,
        })) || [];

        return (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {question.title}
              {question.required && <span className="text-red-500 ml-1">*</span>}
            </label>
            <p className="text-sm text-gray-600 mb-3">Select all that apply</p>
            <MultiSelect
              id={`preview-${question.id}`}
              label=""
              selectedValues={previewMultiValue}
              onChange={setPreviewMultiValue}
              options={checkboxOptions}
              required={question.required}
              helpText={question.settings?.helpText}
              showOtherOption={question.settings?.showOtherOption}
            />
          </div>
        );

      case 'dropdown':
        const dropdownOptions: DropdownOption[] = question.options?.map(opt => ({
          value: opt.value,
          label: opt.label,
        })) || [];

        return (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {question.title}
              {question.required && <span className="text-red-500 ml-1">*</span>}
            </label>
            <p className="text-sm text-gray-600 mb-3">Choose from the list</p>
            <Dropdown
              id={`preview-${question.id}`}
              label=""
              value={previewValue}
              onChange={setPreviewValue}
              options={dropdownOptions}
              placeholder={question.placeholder || 'Select an option'}
              required={question.required}
              helpText={question.settings?.helpText}
            />
          </div>
        );

      case 'rating':
        const ratingMax = question.settings?.maxValue || 5;
        const ratingMin = question.settings?.minValue || 1;
        const ratingLabels = question.settings?.scaleLabels || [];
        const ratingHelpText = question.settings?.helpText;

        return (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {question.title}
              {question.required && <span className="text-red-500 ml-1">*</span>}
            </label>
            {ratingHelpText && (
              <p className="text-sm text-gray-500 mb-3">{ratingHelpText}</p>
            )}
            <div className="flex items-center gap-2">
              {Array.from({ length: ratingMax - ratingMin + 1 }, (_, i) => i + ratingMin).map((value) => (
                <button
                  key={value}
                  type="button"
                  onClick={() => setPreviewValue(value)}
                  className={`w-10 h-10 rounded-lg border-2 transition-all ${
                    previewValue === value
                      ? 'bg-yellow-400 border-yellow-500 text-white'
                      : 'bg-white border-gray-300 text-gray-700 hover:border-yellow-400'
                  }`}
                >
                  ⭐
                </button>
              ))}
            </div>
            {ratingLabels.length > 0 && (
              <div className="flex justify-between mt-2 text-xs text-gray-500">
                <span>{ratingLabels[0]}</span>
                <span>{ratingLabels[ratingLabels.length - 1]}</span>
              </div>
            )}
          </div>
        );

      case 'scale':
        const scaleMax = question.settings?.maxValue || 5;
        const scaleMin = question.settings?.minValue || 1;
        const scaleLabels = question.settings?.scaleLabels || [];
        const scaleHelpText = question.settings?.helpText;

        return (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {question.title}
              {question.required && <span className="text-red-500 ml-1">*</span>}
            </label>
            {scaleHelpText && (
              <p className="text-sm text-gray-500 mb-3">{scaleHelpText}</p>
            )}
            <div className="space-y-2">
              {Array.from({ length: scaleMax - scaleMin + 1 }, (_, i) => i + scaleMin).map((value, index) => (
                <label
                  key={value}
                  className={`flex items-center p-3 border-2 rounded-lg cursor-pointer transition-all ${
                    previewValue === value
                      ? 'bg-blue-50 border-blue-500'
                      : 'bg-white border-gray-200 hover:border-blue-300'
                  }`}
                >
                  <input
                    type="radio"
                    name={`preview-scale-${question.id}`}
                    value={value}
                    checked={previewValue === value}
                    onChange={() => setPreviewValue(value)}
                    className="h-4 w-4 text-blue-600 border-gray-300 focus:ring-blue-500"
                  />
                  <span className="ml-3 flex-1">
                    <span className="font-medium text-gray-900">{value}</span>
                    {scaleLabels[index] && (
                      <span className="text-gray-600 ml-2">- {scaleLabels[index]}</span>
                    )}
                  </span>
                </label>
              ))}
            </div>
          </div>
        );

      case 'yesno':
        const yesNoOptions: RadioOption[] = [
          { value: 'yes', label: 'Yes' },
          { value: 'no', label: 'No' },
        ];

        return (
          <RadioGroup
            id={`preview-${question.id}`}
            label={question.title}
            value={previewValue}
            onChange={setPreviewValue}
            options={yesNoOptions}
            required={question.required}
            helpText={question.settings?.helpText}
            layout="horizontal"
          />
        );

      case 'date':
        const dateHelpText = question.settings?.helpText;
        return (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {question.title}
              {question.required && <span className="text-red-500 ml-1">*</span>}
            </label>
            {dateHelpText && (
              <p className="text-sm text-gray-500 mb-2">{dateHelpText}</p>
            )}
            <input
              type="date"
              value={previewValue}
              onChange={(e) => setPreviewValue(e.target.value)}
              min={question.settings?.minDate}
              max={question.settings?.maxDate}
              required={question.required}
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary"
            />
          </div>
        );

      case 'dosing_schedule': {
        const defaultDays = [true, true, true, true, true, false, false];
        const dsVal: DosingScheduleValue = typeof previewValue === 'object' && previewValue && 'selectedDays' in previewValue
          ? previewValue
          : { daysOn: 5, daysOff: 2, selectedDays: [...defaultDays], cycleDuration: 4, cycleDurationUnit: 'weeks' as const, frequencyPerDay: 1 };
        const dsHelpText = question.settings?.helpText;
        const dsDayLabels = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
        const dsSelected = dsVal.selectedDays && dsVal.selectedDays.length === 7 ? dsVal.selectedDays : defaultDays;
        const dsOn = dsSelected.filter(Boolean).length;
        const dsOff = 7 - dsOn;
        const dsFreq = dsVal.frequencyPerDay || 1;
        const dsFreqLabels: Record<number, string[]> = {
          1: ['Dose'], 2: ['Morning', 'Evening'], 3: ['Morning', 'Midday', 'Evening'],
        };
        const dsDoseLabels = dsFreqLabels[dsFreq] || Array.from({ length: dsFreq }, (_, i) => `Dose ${i + 1}`);
        const dsDoseAmounts = dsVal.doseAmounts && dsVal.doseAmounts.length === dsFreq
          ? dsVal.doseAmounts
          : dsDoseLabels.map((label, i) => ({ label, amount: i === 0 && dsVal.doseAmount ? dsVal.doseAmount : 0 }));
        const dsDoseUnits = question.settings?.availableDoseUnits || ['mL', 'IU', 'mcg'];
        const dsToggle = (idx: number) => {
          const newDays = [...dsSelected];
          newDays[idx] = !newDays[idx];
          if (newDays.filter(Boolean).length === 0) return;
          const on = newDays.filter(Boolean).length;
          setPreviewValue({ ...dsVal, selectedDays: newDays, daysOn: on, daysOff: 7 - on });
        };
        const dsSetFreq = (newFreq: number) => {
          const labels = dsFreqLabels[newFreq] || Array.from({ length: newFreq }, (_, i) => `Dose ${i + 1}`);
          if (newFreq === 1) {
            setPreviewValue({ ...dsVal, frequencyPerDay: 1, doseAmounts: undefined, doseAmount: dsDoseAmounts[0]?.amount || dsVal.doseAmount });
          } else {
            const newAmounts = labels.map((label, i) => ({ label, amount: dsDoseAmounts[i]?.amount || (i === 0 ? (dsVal.doseAmount || 0) : 0) }));
            setPreviewValue({ ...dsVal, frequencyPerDay: newFreq, doseAmounts: newAmounts });
          }
        };
        const dsTotalWeeks = dsVal.cycleDurationUnit === 'months' ? dsVal.cycleDuration * 4.33 : dsVal.cycleDuration;
        const dsTotalDays = Math.round(dsOn * dsTotalWeeks);
        const dsTotalAdmin = dsTotalDays * dsFreq;
        return (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {question.title}
              {question.required && <span className="text-red-500 ml-1">*</span>}
            </label>
            {dsHelpText && <p className="text-sm text-gray-500 mb-3">{dsHelpText}</p>}
            <div className="space-y-4">
              <div>
                <p className="text-xs text-gray-500 mb-2">Which days do you administer? (tap to toggle)</p>
                <div className="flex gap-1.5">
                  {dsDayLabels.map((d, i) => (
                    <button key={i} type="button" onClick={() => dsToggle(i)}
                      className="flex flex-col items-center gap-0.5">
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-medium cursor-pointer transition-all ${
                        dsSelected[i] ? 'bg-blue-500 text-white hover:bg-blue-600' : 'bg-gray-200 text-gray-400 hover:bg-gray-300'
                      }`}>{dsSelected[i] ? '✓' : '–'}</div>
                      <span className={`text-[10px] font-medium ${dsSelected[i] ? 'text-blue-600' : 'text-gray-400'}`}>{d}</span>
                    </button>
                  ))}
                </div>
                <p className="text-xs text-gray-500 mt-1.5">
                  <span className="font-medium text-gray-700">{dsOn} day{dsOn !== 1 ? 's' : ''} on</span>, {dsOff} off per week
                </p>
              </div>
              {/* Frequency */}
              <div>
                <p className="text-xs text-gray-500 mb-1.5">How many times per day do you dose?</p>
                <div className="flex gap-1.5">
                  {[1, 2, 3].map((n) => (
                    <button key={n} type="button" onClick={() => dsSetFreq(n)}
                      className={`px-3 py-1.5 rounded-lg text-xs font-semibold border-2 transition-all ${
                        dsFreq === n ? 'bg-blue-500 text-white border-blue-500' : 'bg-white text-gray-600 border-gray-200 hover:border-blue-300'
                      }`}>{n}x daily</button>
                  ))}
                </div>
              </div>
              {/* Duration */}
              <div>
                <p className="text-xs text-gray-500 mb-1.5">How long do you follow this protocol?</p>
                <div className="flex items-center gap-2">
                  <input
                    type="number"
                    min={1}
                    max={question.settings?.maxCycleDuration || 52}
                    value={dsVal.cycleDuration || ''}
                    onChange={(e) => {
                      const val = parseInt(e.target.value);
                      const max = question.settings?.maxCycleDuration || 52;
                      if (!isNaN(val) && val >= 1 && val <= max) {
                        setPreviewValue({ ...dsVal, cycleDuration: val });
                      } else if (e.target.value === '') {
                        setPreviewValue({ ...dsVal, cycleDuration: 0 });
                      }
                    }}
                    className="w-20 px-3 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary"
                  />
                  <select
                    value={dsVal.cycleDurationUnit}
                    onChange={(e) => setPreviewValue({ ...dsVal, cycleDurationUnit: e.target.value as 'weeks' | 'months' })}
                    className="px-3 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary cursor-pointer"
                  >
                    <option value="weeks">weeks</option>
                    <option value="months">months</option>
                  </select>
                </div>
              </div>
              {/* Dose inputs */}
              {question.settings?.includeDoseAmount !== false && (
                <div>
                  <p className="text-xs text-gray-500 mb-1.5">Dose per administration <span className="text-gray-400">(optional)</span></p>
                  {dsFreq === 1 ? (
                    <div className="flex items-center gap-2">
                      <input type="number" min={0} step="any" value={dsVal.doseAmount ?? ''}
                        onChange={(e) => setPreviewValue({ ...dsVal, doseAmount: e.target.value === '' ? undefined : parseFloat(e.target.value) })}
                        placeholder="e.g., 250"
                        className="w-24 px-3 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary" />
                      <select value={dsVal.doseUnit || dsDoseUnits[0]}
                        onChange={(e) => setPreviewValue({ ...dsVal, doseUnit: e.target.value })}
                        className="px-3 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary cursor-pointer">
                        {dsDoseUnits.map((u) => <option key={u} value={u}>{u}</option>)}
                      </select>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-xs text-gray-500 w-20">Unit:</span>
                        <select value={dsVal.doseUnit || dsDoseUnits[0]}
                          onChange={(e) => setPreviewValue({ ...dsVal, doseUnit: e.target.value })}
                          className="px-3 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary cursor-pointer">
                          {dsDoseUnits.map((u) => <option key={u} value={u}>{u}</option>)}
                        </select>
                      </div>
                      {dsDoseLabels.map((label, i) => (
                        <div key={i} className="flex items-center gap-2">
                          <span className="text-xs font-medium text-gray-600 w-20">{label}:</span>
                          <input type="number" min={0} step="any" value={dsDoseAmounts[i]?.amount || ''}
                            onChange={(e) => {
                              const newAmounts = [...dsDoseAmounts];
                              newAmounts[i] = { ...newAmounts[i], amount: e.target.value === '' ? 0 : parseFloat(e.target.value) };
                              setPreviewValue({ ...dsVal, doseAmounts: newAmounts });
                            }}
                            placeholder={`${label} dose`}
                            className="w-24 px-3 py-1.5 text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary" />
                          <span className="text-xs text-gray-500">{dsVal.doseUnit || dsDoseUnits[0]}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
              {/* Summary */}
              {dsOn > 0 && dsVal.cycleDuration > 0 && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm text-blue-800">
                  <p className="font-medium">
                    {dsDayLabels.filter((_, i) => dsSelected[i]).join(', ')} ({dsOn} on, {dsOff} off) &middot; {dsFreq}x daily for {dsVal.cycleDuration} {dsVal.cycleDurationUnit}
                  </p>
                  <p className="text-xs mt-1">
                    {dsTotalAdmin} total administration{dsTotalAdmin !== 1 ? 's' : ''}
                  </p>
                </div>
              )}
            </div>
          </div>
        );
      }

      case 'measurement': {
        const mVal: MeasurementValue = typeof previewValue === 'object' && previewValue
          ? previewValue
          : { amount: '' as any, unit: question.settings?.defaultMeasurementUnit || 'mL' };
        const mUnits = question.settings?.measurementUnits || ['mg', 'mL', 'g', 'IU', 'mcg', 'units'];
        const mHelpText = question.settings?.helpText;
        return (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {question.title}
              {question.required && <span className="text-red-500 ml-1">*</span>}
            </label>
            {mHelpText && <p className="text-sm text-gray-500 mb-2">{mHelpText}</p>}
            <div className="flex items-center gap-2">
              <input
                type="number"
                value={mVal.amount ?? ''}
                onChange={(e) => setPreviewValue({ ...mVal, amount: e.target.value === '' ? undefined : parseFloat(e.target.value) })}
                placeholder="Enter amount"
                className="flex-1 px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary"
              />
              <select
                value={mVal.unit}
                onChange={(e) => setPreviewValue({ ...mVal, unit: e.target.value })}
                className="w-24 px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-primary"
              >
                {mUnits.map((u) => <option key={u} value={u}>{u}</option>)}
              </select>
            </div>
          </div>
        );
      }

      default:
        return (
          <div className="text-gray-500 italic">
            Preview not available for this question type
          </div>
        );
    }
  };

  return (
    <div className="bg-gray-50 border-2 border-gray-300 rounded-lg p-6">
      <div className="flex items-center gap-2 mb-4 pb-3 border-b border-gray-300">
        <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
        </svg>
        <h4 className="font-semibold text-gray-700 text-sm uppercase tracking-wide">Live Preview</h4>
      </div>

      <div className="bg-white rounded-lg p-5 border border-gray-200 shadow-sm">
        {renderPreviewInput()}
      </div>

      <div className="mt-3 text-xs text-gray-500 flex items-center gap-1">
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
        <span>This is how the question will appear to survey respondents</span>
      </div>
    </div>
  );
};

export default QuestionPreview;